import React, { useEffect, useMemo, useState } from 'react';
import { Grid, Paper } from '@mui/material';
import * as machineDataApi from '../../../api/app/machineDataApi';
import * as assetApi from '../../../api/app/assetApi';
import * as commentApi from '../../../api/app/performanceCommentApi';
import { useQuery } from 'react-query';
import { Asset, AssetDashboardSetting, Shift } from '../../../types/app/asset.types';
import { MonitoredCounter } from '../../../types/app/monitoredCounter.types';
import { MonitoredCycleTime } from '../../../types/app/monitoredCycleTime.types';
import { MonitoredState } from '../../../types/app/monitoredState.types';
import { ChartsDataProcessProfilerPerformance } from '../../../components/app/DataProcess/ChartsDataProcessProfilerPerformance';
import { Moment } from 'moment';
import moment from 'moment-timezone';
import { generalDateTimeWithSecondsShowFormat, utcFormat } from '../../../types/core/date.types';
import { convertGranularityToMinutes } from '../../../utils/core/functions/convertDateTime';
import { StatsDataLoaderTop } from './Stats/StatsDataLoaderTop';
import { StatsDataLoaderBottom } from './Stats/StatsDataLoaderBottom';
import { useTimer } from '../../../hooks/useTimer';
import {
  getFullShiftDateTime,
  getPreviousShift,
} from '../../../utils/core/functions/shift.functions';
import { ChartTypes } from '../../../types/app/chart.types';
import { Loader } from '../../../components/core/Loader/Loader';
import { Typography } from '@mui/material';
import { configNoCache } from '../../../api/core/api';
import { useTranslation } from '../../../contexts/translation';
import { PerformanceCommentModal } from '../../../components/app/PerformanceComment/PerformanceComment';
import { InteractionItem } from 'chart.js';
import { DateTimeIntegerProcess } from '../../../types/app/dataProcess';
import { ThemeType, useLocalTheme } from '../../../hooks/useLocalTheme';
import { GRAY } from '../../../utils/core/functions/colors.function';
import { isExtraStatsLine } from '../../../utils/core/functions/dataProcess.functions';
import { ORANGE } from '../../../utils/core/functions/colors.function';
import { DARK_ORANGE } from '../../../utils/core/functions/colors.function';
import { GREEN, DARK_GREEN } from '../../../utils/core/functions/colors.function';

export interface Props {
  asset: Asset;
  mainCounters: MonitoredCounter[] | null;
  mainCycleTime: MonitoredCycleTime | null;
  mainMonitoredState: MonitoredState | null;
  timezone: string;
}

export function DashboardDataLoader({
  asset,
  mainCounters,
  mainCycleTime,
  mainMonitoredState,
  timezone,
}: Props) {
  const { t } = useTranslation();
  const { localTheme } = useLocalTheme();
  const today: Moment = moment().utc();
  const [open, setOpen] = useState<boolean>(false);
  const [processDataObject, setProcessDataObject] = useState<any>();
  // Začátek a konec směny
  const [currentShiftStart, setCurrentShiftStart] = useState<Moment | null>(null);
  const [currentShiftEnd, setCurrentShiftEnd] = useState<Moment | null>(null);
  // Začátky a konce předchozích směn
  const [historyShiftsStart, setHistoryShiftsStart] = useState<Moment[] | null>(null);
  const [historyShiftsEnd, setHistoryShiftsEnd] = useState<Moment[] | null>(null);
  const [isCurrentShift, setIsCurrentShift] = useState<boolean>(false);
  const [isAllShifts, setIsAllShifts] = useState<boolean>(false);
  // Předchozí 2 směny
  // TODO: Zobecnit na pole
  const [historyShift_1, setHistoryShift_1] = useState<Shift | null>(null);
  const [historyShift_2, setHistoryShift_2] = useState<Shift | null>(null);
  const [historyShift_1_combined, setHistoryShift_1_combined] = useState<any[]>([]);
  const [historyShift_2_combined, setHistoryShift_2_combined] = useState<any[]>([]);
  const [isCompact, setIsCompact] = useState<boolean>(false);
  // Časovač
  // TODO: Nebylo by lepší přesunout do stats loaderu?
  const { timer } = useTimer();

  // mainDashboardů může být víc --> chci string, oddělený čárkou: "qty,qty2,qty3..."
  const getJoinedKeysFromArr = (arr: any[], key: string) => {
    if (!arr) return '';
    let val = '';
    arr.map((item: any, index: number) => {
      val += index === 0 ? item[key] : ',' + item[key];
    });
    return val;
  };

  // spojit countery
  const combineCounters = (counterData: any[]) => {
    let combinedArr: any[] = [];
    counterData.map((c: any) => {
      c.values.map((val: any) => {
        let index = combinedArr.findIndex((item: any) => item.time === val.time);
        if (index > -1) {
          // je v poli -->  sečíst hodnoty
          combinedArr[index].value += val.value;
        } else {
          // není v poli --> přidat
          combinedArr = [...combinedArr, val];
        }
      });
    });
    return combinedArr;
  };

  // Config pro main dashboard
  const { data: dashboardConfig } = useQuery<AssetDashboardSetting>(['dashboardConfig'], () =>
    assetApi.getMainDashBoardSettingForAsset(asset.id),
  );

  // Data pro aktuální směnu (real, plan and stats).
  const { data: currentShiftData } = useQuery(
    ['currentShiftDataByDate'],
    () =>
      machineDataApi.getDataForMainDashboard({
        assetId: asset.id,
        machine: asset.influxMachineName,
        dateFrom: currentShiftStart!.format(utcFormat),
        dateTo: currentShiftEnd!.format(utcFormat),
        dataId: getJoinedKeysFromArr(mainCounters!, 'influxValueName'),
        cycleTimeDataId: mainCycleTime?.influxValueName,
        stateDataId: mainMonitoredState?.influxValueName,
        aggregation: dashboardConfig?.timeAggregation,
        hydraMachineNr: asset.hydraMachineNumber,
      }),
    {
      ...configNoCache,
      enabled: !!currentShiftStart && !!currentShiftEnd && !!dashboardConfig,
      refetchInterval: 5000,
    },
  );

  // Načtení aktuální směny - název, od/do
  const { data: currentShift, isFetching: currentShiftFetching } = useQuery<Shift>(
    ['currentShift'],
    () => assetApi.getCurrentShiftForAsset(asset.id),
    {
      onSuccess: (result) => setIsCurrentShift(!!result),
      refetchInterval: 30000,
    },
  );

  // Načteme seznam směn daného assetu
  const { data: allShifts, isLoading: allShiftsLoading } = useQuery<Shift[]>(
    ['allShifts'],
    () => assetApi.getShiftsForAsset(asset.id),
    {
      ...configNoCache,
      onSuccess: (result) => setIsAllShifts(result && result.length > 0),
    },
  );

  // Načteme data pro předchozí směnu 1
  const { data: historyShiftData_1, isLoading: historyShiftData_1Loading } = useQuery(
    ['historyShiftDataByDate_1'],
    () =>
      assetApi.getPerformance({
        assetId: asset.id,
        machine: mainCounters![0].parentAssetInfluxMachineName,
        dateFrom: historyShiftsStart![0].clone().format(utcFormat),
        dateTo: historyShiftsEnd![0].clone().format(utcFormat),
        dataId: getJoinedKeysFromArr(mainCounters!, 'influxValueName'),
        aggregation: dashboardConfig!.timeAggregation,
      }),
    {
      ...configNoCache,
      enabled:
        !!mainCounters &&
        !!historyShiftsStart &&
        !!historyShiftsEnd &&
        !!dashboardConfig &&
        !historyShiftsStart![0].isSame(currentShiftStart),
      onSuccess: (res) => setHistoryShift_1_combined(combineCounters(res?.counterData?.data)),
    },
  );

  // Načteme data pro předchozí směnu 2
  const { data: historyShiftData_2, isLoading: historyShiftData_2Loading } = useQuery(
    ['historyShiftDataByDate_2'],
    () =>
      assetApi.getPerformance({
        assetId: asset.id,
        machine: mainCounters![0].parentAssetInfluxMachineName,
        dateFrom: historyShiftsStart![1].clone().format(utcFormat),
        dateTo: historyShiftsEnd![1].clone().format(utcFormat),
        dataId: getJoinedKeysFromArr(mainCounters!, 'influxValueName'),
        aggregation: dashboardConfig!.timeAggregation,
      }),
    {
      ...configNoCache,
      enabled:
        !!mainCounters &&
        !!historyShiftsStart &&
        !!historyShiftsEnd &&
        !!dashboardConfig &&
        !historyShiftsStart![1].isSame(currentShiftStart),
      onSuccess: (res) => setHistoryShift_2_combined(combineCounters(res?.counterData?.data)),
    },
  );
  const { data: historyShiftComments1 } = useQuery(
    ['historyShiftComments1'],
    () =>
      commentApi.getListFiltered({
        assetId: asset.id,
        from: historyShiftsStart![0].clone().format(utcFormat),
        to: historyShiftsEnd![0].clone().format(utcFormat),
      }),
    {
      ...configNoCache,
      enabled: !!historyShiftsStart && !!historyShiftsEnd,
    },
  );
  const { data: historyShiftComments2 } = useQuery(
    ['historyShiftComments2'],
    () =>
      commentApi.getListFiltered({
        assetId: asset.id,
        from: historyShiftsStart![1].clone().format(utcFormat),
        to: historyShiftsEnd![1].clone().format(utcFormat),
      }),
    {
      ...configNoCache,
      enabled: !!historyShiftsStart && !!historyShiftsEnd,
    },
  );
  const { data: currentShiftComments } = useQuery(
    ['currentShiftComments'],
    () =>
      commentApi.getListFiltered({
        assetId: asset.id,
        from: currentShiftStart!.clone().format(utcFormat),
        to: currentShiftEnd!.clone().format(utcFormat),
      }),
    {
      ...configNoCache,
      enabled: !!currentShiftStart && !!currentShiftEnd,
    },
  );

  const onChartClick = (result: InteractionItem[], processedData: DateTimeIntegerProcess[]) => {
    if (result && result.length > 0) {
      setProcessDataObject(processedData[result[0].index]);
      setOpen(true);
    }
  };

  // Po načtení aktuální směny spočítáme datum začátku a konce směny
  useEffect(() => {
    if (!currentShift) return;
    // If there is already current shift, we want to refresh page (F5)
    if (currentShiftStart) location.reload();
    // Calculate start and end of current shift
    const dateStartEnd = getFullShiftDateTime(today.clone(), currentShift, false);
    setCurrentShiftStart(dateStartEnd.start!);
    setCurrentShiftEnd(dateStartEnd.end!);
  }, [currentShift]);

  useEffect(() => {
    if (window.outerWidth < 800 && window.location.pathname.includes('fullscreen')) {
      setIsCompact(true);
    }
    if (window.outerWidth > 800 && window.location.pathname.includes('fullscreen')) {
      setIsCompact(false);
    }
  }, [window.location.pathname.includes('fullscreen') && window.outerWidth]);

  // Spočítáme dvě předchozí směny
  useEffect(() => {
    if (!allShifts) return;
    let _currentShift = currentShift;
    if (!_currentShift) _currentShift = allShifts[0];

    let _shifts = allShifts;

    // Načteme předchozí směnu
    const shift1 = getPreviousShift(_currentShift, _shifts);
    if (!shift1) return;

    const isOneShift = _shifts.length === 1;
    if (isOneShift) _shifts = allShifts;

    // Načteme druhou předchozí směnu
    const shift2 = getPreviousShift(shift1!, _shifts);
    if (!shift2) return;

    // Spočítáme konkrétní datum směny
    const shift1Full = getFullShiftDateTime(today.clone(), shift1!, isOneShift); // Předchozí směna
    const shift2Full = getFullShiftDateTime(shift1Full.start!.clone(), shift2!, isOneShift); // Předpředchozí

    // Nastavíme states
    setHistoryShiftsStart([shift2Full.start!.clone(), shift1Full.start!.clone()]);
    setHistoryShiftsEnd([shift2Full.end!.clone(), shift1Full.end!.clone()]);
    setHistoryShift_1(shift2!);
    setHistoryShift_2(shift1!);
  }, [allShifts, currentShift]);

  // Graf pro aktuální směnu
  const currentChart = useMemo(() => {
    if (!currentShiftFetching && !isCurrentShift)
      return (
        <Typography style={{ marginTop: '25%', marginLeft: '25%', fontSize: '2em' }}>
          {t('page.dashboard.graph-text-no-active-shifts')}
        </Typography>
      );

    if (!currentShiftData || !currentShiftStart || !currentShiftEnd || !dashboardConfig)
      return <Loader />;
    const combinedValues = combineCounters(currentShiftData.counterQty.data);
    const data = combinedValues.length > 0 ? combinedValues : [];
    const dataPlanned = currentShiftData.plan;
    const configRealTo = data.length > 0 ? data.length - 1 : 0;
    const configPlanFrom = data.length > 0 ? data.length : 0;
    const configPlanTo =
      dataPlanned.length > 0 ? data.length + dataPlanned.length - 1 : data.length;
    return (
      <ChartsDataProcessProfilerPerformance
        title={t('page.dashboard.graph-title-01')}
        data={[...data, ...dataPlanned]}
        chartHeight={
          isCompact
            ? '35vh'
            : window.location.pathname.includes('fullscreen')
            ? isExtraStatsLine(dashboardConfig)
              ? '50vh'
              : '55vh'
            : '45vh'
        }
        intervals={[
          {
            start: currentShiftStart!.clone().add(1, 'hours'),
            end: currentShiftEnd!.clone(),
          },
        ]}
        configs={[
          {
            iFrom: 0,
            iTo: configRealTo,
            dataSetId: 'REAL',
            dataSetValueKey: 'value',
          },
          {
            iFrom: configPlanFrom,
            iTo: configPlanTo,
            dataSetId: 'PLAN',
            dataSetValueKey: 'qty',
          },
          {
            iFrom: configPlanFrom,
            iTo: configPlanTo,
            dataSetId: 'ORANGE_THRESHOLD',
            dataSetValueKey: 'qty',
          },
          {
            iFrom: configPlanFrom,
            iTo: configPlanTo,
            dataSetId: 'GREEN_THRESHOLD',
            dataSetValueKey: 'qty',
          },
          {
            iFrom: 0,
            iTo: configRealTo,
            dataSetId: 'IS_COMMENT',
            dataSetValueKey: 'value',
          },
          {
            iFrom: 0,
            iTo: configRealTo,
            dataSetId: 'TAKT_TIME',
            dataSetValueKey: '',
          },
        ]}
        chartsConfig={[
          {
            dataSetId: 'PLAN',
            type: ChartTypes.LINE,
            label: t('page.dashboard.graph-dataset-label-plan'),
            style: {
              borderColor: localTheme === ThemeType.DARK ? 'rgb(25, 78, 180)' : 'rgb(51, 153, 255)',
              borderWidth: 4,
              fill: false,
            },
          },
          {
            dataSetId: 'ORANGE_THRESHOLD',
            type: ChartTypes.LINE,
            label: '',
            style: {
              borderColor:
                localTheme === ThemeType.DARK
                  ? `rgb(${DARK_ORANGE.r},${DARK_ORANGE.g},${DARK_ORANGE.b})`
                  : `rgb(${ORANGE.r},${ORANGE.g},${ORANGE.b})`,
              pointRadius: 0,
              borderWidth: 3,
              borderDash: [5, 5],
              pointStyle: 'dash',
            },
          },
          {
            dataSetId: 'GREEN_THRESHOLD',
            type: ChartTypes.LINE,
            label: '',
            style: {
              borderColor:
                localTheme === ThemeType.DARK
                  ? `rgb(${DARK_GREEN.r},${DARK_GREEN.g},${DARK_GREEN.b})`
                  : `rgb(${GREEN.r},${GREEN.g},${GREEN.b})`,
              pointRadius: 0,
              borderWidth: 3,
              borderDash: [5, 5],
              pointStyle: 'dash',
            },
          },
          {
            dataSetId: 'TAKT_TIME',
            type: ChartTypes.LINE,
            label: '',
            style: {
              borderColor: `rgb(252, 159, 214)`,
              pointRadius: 0,
              borderWidth: 4,
            },
          },
          {
            dataSetId: 'REAL',
            type: ChartTypes.BAR,
            label: t('page.dashboard.graph-dataset-label-reality'),
            style: {
              borderColor:
                localTheme === ThemeType.DARK ? `rgb(${GRAY.r},${GRAY.g},${GRAY.b})` : 'white',
              borderWidth: 2,
              fill: false,
            },
            isCurrent: true,
          },
          {
            dataSetId: 'IS_COMMENT',
            type: ChartTypes.LINE,
            label: t('page.dashboard.graph-dataset-label-comments'),
            style: {
              borderColor: 'rgb(249, 215, 28)',
              borderWidth: 8,
              fill: false,
              showLine: false,
              pointStyle: 'triangle',
            },
          },
        ]}
        granularity={convertGranularityToMinutes(dashboardConfig?.timeAggregation)}
        dashboardConfig={dashboardConfig}
        timezone={timezone}
        commentList1={currentShiftComments}
        onChartClick={onChartClick}
      />
    );
  }, [
    isCurrentShift,
    currentShiftData,
    currentShift,
    currentShiftStart,
    currentShiftEnd,
    dashboardConfig,
    currentShiftComments,
    timezone,
  ]);

  // Graf pro 2 předchozí směny.
  const historyChart = useMemo(() => {
    if (
      historyShiftData_1Loading ||
      historyShiftData_2Loading ||
      !historyShiftsStart ||
      !historyShiftsEnd ||
      !dashboardConfig ||
      !historyShift_1_combined ||
      !historyShift_2_combined
    )
      return <Loader />;

    // Skutečnost směna 1 data, index OD/OD
    let data_1 = [];
    if (historyShiftData_1 && historyShiftData_1.counterData.data.length > 0)
      data_1 = historyShiftData_1.counterData.data.length > 0 ? historyShift_1_combined : [];
    const configRealFrom_1 = 0;
    const configRealTo_1 = data_1.length > 0 ? data_1.length - 1 : 0;
    // Skutečnost směna 2 data, index OD/OD
    let data_2 = [];
    if (historyShiftData_2 && historyShiftData_2.counterData.data.length > 0)
      data_2 = historyShiftData_2.counterData.data.length > 0 ? historyShift_2_combined : [];
    const configRealFrom_2 =
      configRealTo_1 > 0
        ? configRealTo_1 + 1
        : data_1.length > 0 && data_2.length > 0
        ? configRealTo_1 + 1
        : configRealTo_1;
    const configRealTo_2 =
      data_2.length > 0 ? configRealFrom_2 + data_2.length - 1 : configRealFrom_2;
    // Plan směna 1 data, index OD/DO
    const dataPlanned_1 = historyShiftData_1 ? historyShiftData_1.plannedData : [];
    const configPlanFrom_1 =
      configRealTo_2 > 0
        ? configRealTo_2 + 1
        : data_1.length > 0 || data_2.length > 0
        ? configRealTo_2 + 1
        : configRealTo_2;
    const configPlanTo_1 =
      dataPlanned_1.length > 0 ? configPlanFrom_1 + dataPlanned_1.length - 1 : configPlanFrom_1;

    // Plan směna 2 data, index OD/DO
    const dataPlanned_2 = historyShiftData_2 ? historyShiftData_2.plannedData : [];
    const configPlanFrom_2 = configPlanTo_1 > 0 ? configPlanTo_1 + 1 : configPlanTo_1;
    const configPlanTo_2 =
      dataPlanned_2.length > 0 ? configPlanFrom_2 + dataPlanned_2.length - 1 : configPlanFrom_2;

    // Intervals
    const intervals = historyShiftsStart.map((start, i) => {
      return {
        start: start.clone().add(1, 'hours'),
        end: historyShiftsEnd[i].clone(),
      };
    });

    return (
      <ChartsDataProcessProfilerPerformance
        title={t('page.dashboard.graph-title-02')}
        data={[...data_1, ...data_2, ...dataPlanned_1, ...dataPlanned_2]}
        intervals={intervals}
        chartHeight={
          isCompact
            ? '35vh'
            : window.location.pathname.includes('fullscreen')
            ? isExtraStatsLine(dashboardConfig)
              ? '50vh'
              : '55vh'
            : '45vh'
        }
        configs={[
          {
            iFrom: configRealFrom_1,
            iTo: configRealTo_2,
            dataSetId: 'REAL',
            dataSetValueKey: 'value',
          },
          {
            iFrom: configPlanFrom_1,
            iTo: configPlanTo_2,
            dataSetId: 'PLAN',
            dataSetValueKey: 'qty',
          },
          {
            iFrom: configPlanFrom_1,
            iTo: configPlanTo_2,
            dataSetId: 'ORANGE_THRESHOLD',
            dataSetValueKey: 'qty',
          },
          {
            iFrom: configPlanFrom_1,
            iTo: configPlanTo_2,
            dataSetId: 'GREEN_THRESHOLD',
            dataSetValueKey: 'qty',
          },
          {
            iFrom: configRealFrom_1,
            iTo: configRealTo_2,
            dataSetId: 'IS_COMMENT',
            dataSetValueKey: 'value',
          },
          {
            iFrom: 0,
            iTo: configRealTo_2,
            dataSetId: 'TAKT_TIME',
            dataSetValueKey: '',
          },
        ]}
        chartsConfig={[
          {
            dataSetId: 'PLAN',
            type: ChartTypes.LINE,
            label: t('page.dashboard.graph-dataset-label-plan'),
            style: {
              borderColor: localTheme === ThemeType.DARK ? 'rgb(25, 78, 180)' : 'rgb(51, 153, 255)',
              borderWidth: 4,
              fill: false,
            },
          },
          {
            dataSetId: 'ORANGE_THRESHOLD',
            type: ChartTypes.LINE,
            label: '',
            style: {
              borderColor:
                localTheme === ThemeType.DARK
                  ? `rgb(${DARK_ORANGE.r},${DARK_ORANGE.g},${DARK_ORANGE.b})`
                  : `rgb(${ORANGE.r},${ORANGE.g},${ORANGE.b})`,
              pointRadius: 0,
              borderWidth: 3,
              borderDash: [5, 5],
              pointStyle: 'dash',
            },
          },
          {
            dataSetId: 'GREEN_THRESHOLD',
            type: ChartTypes.LINE,
            label: '',
            style: {
              borderColor:
                localTheme === ThemeType.DARK
                  ? `rgb(${DARK_GREEN.r},${DARK_GREEN.g},${DARK_GREEN.b})`
                  : `rgb(${GREEN.r},${GREEN.g},${GREEN.b})`,
              pointRadius: 0,
              borderWidth: 3,
              borderDash: [5, 5],
              pointStyle: 'dash',
            },
          },
          {
            dataSetId: 'TAKT_TIME',
            type: ChartTypes.LINE,
            label: '',
            style: {
              borderColor: `rgb(252, 159, 214)`,
              pointRadius: 0,
              borderWidth: 4,
            },
          },
          {
            dataSetId: 'REAL',
            type: ChartTypes.BAR,
            label: t('page.dashboard.graph-dataset-label-reality'),
            style: {
              borderColor:
                localTheme === ThemeType.DARK ? `rgb(${GRAY.r},${GRAY.g},${GRAY.b})` : 'white',
              borderWidth: 2,
              fill: false,
            },
          },
          {
            dataSetId: 'IS_COMMENT',
            type: ChartTypes.LINE,
            label: t('page.dashboard.graph-dataset-label-comments'),
            style: {
              borderColor: 'rgb(249, 215, 28)',
              borderWidth: 5,
              fill: false,
              showLine: false,
              pointStyle: 'triangle',
            },
          },
        ]}
        granularity={convertGranularityToMinutes(dashboardConfig.timeAggregation)}
        dashboardConfig={dashboardConfig}
        commentList1={historyShiftComments1}
        commentList2={historyShiftComments2}
        onChartClick={onChartClick}
        timezone={timezone}
      />
    );
  }, [
    historyShiftData_1,
    historyShiftData_2,
    historyShiftsStart,
    historyShiftsEnd,
    dashboardConfig,
    historyShiftComments1,
    historyShiftComments2,
    timezone,
  ]);
  // Textové údaje nad grafy
  const statsTop = useMemo(() => {
    if (!currentShiftData || !currentShift || !today) return;
    // TODO: Založit čísleník pro stavy
    const state = currentShiftData.stateActual === 0 ? 'Stojí' : 'Běží';
    return (
      <StatsDataLoaderTop
        currentShiftTitle={currentShift.name}
        timer={timer.tz(timezone).format(generalDateTimeWithSecondsShowFormat)}
        state={state}
        planSum={currentShiftData.planSum}
        counterQtySum={currentShiftData.counterQtySum}
        performance={currentShiftData.performance}
        performanceActualBlock={currentShiftData.performanceActualBlock}
        planActualBlock={currentShiftData.planActualBlock}
        qtyActualBlock={currentShiftData.qtyActualBlock}
        scrapSum={currentShiftData.scrapSum}
        cycleTimeActual={currentShiftData.cycleTimeActual}
        hydraDataResult={currentShiftData.hydraDataResult}
        dashboardConfig={dashboardConfig}
        isCompact={isCompact}
        localTheme={localTheme}
        asset={asset}
      />
    );
  }, [currentShiftData, currentShift, timer]);

  // Textové údaje pod grafem předchozích směn
  const statsBottom = useMemo(() => {
    if (!historyShiftData_1 || !historyShiftData_2 || !historyShift_1 || !historyShift_2) return;
    return (
      <StatsDataLoaderBottom
        shift_1={historyShift_1}
        dataShift_1={historyShiftData_1}
        shift_2={historyShift_2}
        dataShift_2={historyShiftData_2}
        localTheme={localTheme}
        asset={asset}
      />
    );
  }, [historyShiftData_1, historyShiftData_2, historyShift_1, historyShift_2]);

  if (!allShiftsLoading && !isAllShifts)
    return (
      <Typography style={{ textAlign: 'center', marginTop: '15%', fontSize: '2em' }}>
        {t('page.dashboard.graph-text-no-shifts')}
      </Typography>
    );

  return (
    <Grid container>
      <Grid item xs={12} md={12} order={{ xs: 1, md: 1 }}>
        {statsTop}
      </Grid>
      <Grid item xs={12} md={5} order={{ xs: 3, md: 2 }} mt={{ md: 4 }}>
        {historyChart}
      </Grid>
      <Grid item xs={12} md={7} order={{ xs: 2, lg: 3 }}>
        {currentChart}
      </Grid>
      <Grid item xs={12} md={dashboardConfig?.note ? 7 : 12} lg={dashboardConfig?.note ? 7 : 9} order={{ xs: 4, lg: 4 }}>
        {statsBottom}
      </Grid>
      {dashboardConfig?.note && (
        <Grid item xs={12} md={5} lg={5} order={{ xs: 5, lg: 4 }} sx={{ display: 'flex', alignItems: 'center', pl: { md: 30 } }}>
          <Typography style={{
            whiteSpace: 'pre-line',
            fontSize: window.location.pathname.includes('fullscreen') ? '1.75rem' : '1.4rem',
            color: '#E98300',
            letterSpacing: '0px',
            wordSpacing: '0px',
          }}>{dashboardConfig.note}</Typography>
        </Grid>
      )}
      <PerformanceCommentModal
        open={open}
        setOpen={setOpen}
        processDataObject={processDataObject}
        asset={asset}
        granularity={convertGranularityToMinutes(dashboardConfig?.timeAggregation!)}
        timezone={timezone}
      />
    </Grid>
  );
}
